import {
  PreviewBlock,
  StyledFlowNote,
  StyledPanelHeader,
  StyledPanelTitle,
  StyledPreviewPanel,
  StyledVariantPill,
} from "../preview";
import {
  StyledAmountRow,
  StyledCardRow,
  StyledDetailLine,
  StyledDivider,
  StyledFooterLogo,
  StyledInstallmentNote,
  StyledMessageText,
  StyledReceipt,
  StyledReceiptBody,
  StyledReceiptFooter,
  StyledReceiptHeading,
  StyledReceiptSubheading,
  StyledSection,
  StyledSectionTitle,
  StyledSignLine,
  StyledSubsection,
} from "./styled";

/**
 * Stand-in order behind the mock. The figures are internally consistent — the
 * two payments add up to the total paid, and paid plus remaining matches the
 * providers' final balances — so the receipt reads like a real one.
 */
const previewReceipt = {
  facilityName: "General Hospital",
  address: "123 Main Street, Springfield, IL, 62704",
  patientName: "Jane Q. Patient",
  visitDate: "03/14/2026",
  accountNumber: "100234",
  insurance: "Blue Cross PPO",
  procedures: [
    { code: "45380", description: "Colonoscopy with biopsy" },
    { code: "00810", description: "Anesthesia for lower intestinal endoscopy" },
  ],
  firstPaymentDate: "03/01/2026",
  secondPaymentDate: "04/01/2026",
  installmentAmount: "$210.00",
  totalPaid: "$420.00",
  remaining: "$420.00",
  remainingInstallments: "2",
  nextDue: "05/01/2026",
  card: "Card (type: VISA) ending 4242",
};

/** Per-provider figures for the version 2 breakdown. */
const previewBreakdown = [
  {
    providerName: "General Hospital",
    lastPaymentAmount: "$150.00",
    totalAmountPaid: "$300.00",
    transfers: "$0.00",
    finalBalance: "$600.00",
  },
  {
    providerName: "Springfield Anesthesia Group",
    lastPaymentAmount: "$60.00",
    totalAmountPaid: "$120.00",
    transfers: "$0.00",
    finalBalance: "$240.00",
  },
];

export interface IReceiptPreviewProps {
  version2Receipt?: boolean;
  patientSignature?: boolean;
}

/**
 * Compact mock of the receipt a patient gets after paying, mirroring
 * ReceiptDefault so the toggles show their effect before they are saved.
 * Values are placeholders — only the layout is real.
 *
 * The sample is a payment-plan order, which is what makes the breakdown's last
 * payment row and the reallocation message visible; a receipt for an order paid
 * in full shows the same block without those two.
 */
export function ReceiptPreview({
  version2Receipt,
  patientSignature,
}: IReceiptPreviewProps) {
  return (
    <StyledPreviewPanel data-test-id="customize-receipt-preview">
      <StyledPanelHeader>
        <StyledPanelTitle>
          <i className="pi pi-receipt" />
          PATIENT RECEIPT
        </StyledPanelTitle>
        <StyledVariantPill>
          {version2Receipt ? "Version 2 receipt" : "Standard receipt"}
        </StyledVariantPill>
      </StyledPanelHeader>

      <StyledFlowNote>
        <i className="pi pi-info-circle" />
        <span>
          Patients open this receipt after paying, and staff can open it from
          the estimate page. It is generated as a PDF either way.
        </span>
      </StyledFlowNote>

      {version2Receipt && (
        <StyledFlowNote $tone="warn">
          <i className="pi pi-exclamation-triangle" />
          <span>
            The breakdown is only printed for estimates that have a per-provider
            payment breakdown. Other estimates keep the standard receipt.
          </span>
        </StyledFlowNote>
      )}

      <StyledReceipt>
        <StyledReceiptBody>
          <StyledReceiptHeading>Payment Receipt</StyledReceiptHeading>
          <StyledReceiptSubheading>
            {previewReceipt.facilityName}
          </StyledReceiptSubheading>
          <StyledReceiptSubheading>
            {previewReceipt.address}
          </StyledReceiptSubheading>

          <StyledCardRow>
            <StyledSubsection>
              <StyledSectionTitle>Patient Details</StyledSectionTitle>
              <StyledDetailLine>
                <strong>Patient:</strong> {previewReceipt.patientName}
              </StyledDetailLine>
              <StyledDetailLine>
                <strong>Visit Date:</strong> {previewReceipt.visitDate}
              </StyledDetailLine>
              <StyledDetailLine>
                <strong>Account Number:</strong> {previewReceipt.accountNumber}
              </StyledDetailLine>
              <StyledDetailLine>
                <strong>Insurance:</strong> {previewReceipt.insurance}
              </StyledDetailLine>
            </StyledSubsection>

            <StyledSubsection>
              <StyledSectionTitle>Service Details</StyledSectionTitle>
              {previewReceipt.procedures.map((procedure) => (
                <StyledDetailLine key={procedure.code}>
                  <strong>{procedure.code}</strong> - {procedure.description}
                </StyledDetailLine>
              ))}
            </StyledSubsection>
          </StyledCardRow>

          <StyledSection>
            <StyledSectionTitle>Payment Summary</StyledSectionTitle>
            <StyledAmountRow>
              <span>Total Paid:</span>
              <span>{previewReceipt.totalPaid}</span>
            </StyledAmountRow>
            <StyledAmountRow $sub>
              <span>Paid on: {previewReceipt.firstPaymentDate}</span>
              <span>{previewReceipt.installmentAmount}</span>
            </StyledAmountRow>
            <StyledAmountRow $sub>
              <span>Paid on: {previewReceipt.secondPaymentDate}</span>
              <span>{previewReceipt.installmentAmount}</span>
            </StyledAmountRow>

            <StyledDivider />

            <StyledAmountRow $accent>
              <span>Total</span>
              <span>{previewReceipt.totalPaid}</span>
            </StyledAmountRow>
            <StyledAmountRow $accent>
              <span>Remaining:</span>
              <span>{previewReceipt.remaining}</span>
            </StyledAmountRow>
            <StyledInstallmentNote>
              {previewReceipt.remainingInstallments} payments of{" "}
              {previewReceipt.installmentAmount} /month Next Due:{" "}
              {previewReceipt.nextDue}
            </StyledInstallmentNote>

            <StyledAmountRow>
              <span>Method:</span>
              <span>{previewReceipt.card}</span>
            </StyledAmountRow>
          </StyledSection>

          {version2Receipt && (
            <PreviewBlock tag="Version 2 Receipt">
              <StyledSubsection>
                <StyledSectionTitle>
                  Payment Breakdown by Provider
                </StyledSectionTitle>
                {previewBreakdown.map((provider) => (
                  <StyledSection key={provider.providerName}>
                    <StyledSectionTitle>
                      {provider.providerName}
                    </StyledSectionTitle>
                    <StyledAmountRow>
                      <span>
                        Last Payment on: {previewReceipt.secondPaymentDate}
                      </span>
                      <span>{provider.lastPaymentAmount}</span>
                    </StyledAmountRow>
                    <StyledAmountRow>
                      <span>Total Paid:</span>
                      <span>{provider.totalAmountPaid}</span>
                    </StyledAmountRow>
                    <StyledAmountRow>
                      <span>Refunds/Transfers:</span>
                      <span>{provider.transfers}</span>
                    </StyledAmountRow>
                    <StyledDivider />
                    <StyledAmountRow>
                      <span>Final Balance:</span>
                      <span>{provider.finalBalance}</span>
                    </StyledAmountRow>
                  </StyledSection>
                ))}
              </StyledSubsection>

              <StyledSection>
                <StyledMessageText>
                  We&apos;ll reallocate your payments if, after insurance
                  adjustments, your responsibility shifts from one provider to
                  another. This can happen when one provider bills earlier than
                  expected.
                </StyledMessageText>
              </StyledSection>
            </PreviewBlock>
          )}
        </StyledReceiptBody>

        {patientSignature && (
          <PreviewBlock tag="Patient Signature">
            <StyledSignLine>Sign here</StyledSignLine>
          </PreviewBlock>
        )}

        <StyledReceiptFooter>
          <StyledFooterLogo>LOGO</StyledFooterLogo>
          <span>www.clearhealthinc.com</span>
          <span>888-918-2522 | support@clearhealthinc.com</span>
          <span>Clear Health Inc. All rights reserved.</span>
        </StyledReceiptFooter>
      </StyledReceipt>
    </StyledPreviewPanel>
  );
}
